import { v4 as uuidv4 } from "uuid";
import type { PocAssignmentDoc } from "../../../../../data-model/pocAssignmentRule";
import type { QueryBuilderValue } from "../../../../../data-model/queryBuilder";
import { DEFAULT_POC_ASSIGNMENT_DOC_ID } from "../../pocAssignment/pocAssignmentUtils";
import type { PocConfigTableRowState } from "../data-model/pocAssignmentModel";
import {
  ALL_COHORT_EXPAND_TARGETS,
  isAllCohort,
  normalizeAvailability,
} from "./pocAssignmentConstants";

const RULE_SUBTEXT = "Auto-generated from the POC config table";
const POC_ACTION_FIELD = "poc";
/** Condition field names + operators as persisted in the rules doc. */
const FILENAME_FIELD = "filename";
const FILENAME_OPERATOR = "contains_any_of";
const DRUG_COHORT_FIELD = "drug_cohort";
const DRUG_COHORT_OPERATOR = "=";
const ORG_ID_FIELD = "org_id";
const ORG_ID_OPERATOR = "=";

/** Distinct, truthy values preserving first-seen order. */
const distinct = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)));

/**
 * One (cohort, batch) group: the single batch and every available POC that
 * actually owns that batch within the cohort. Grouping at the batch level (not
 * the cohort level) is what preserves the per-POC batch ownership, so a POC can
 * only ever be assigned an order for a batch they own.
 */
interface BatchGroup {
  cohort: string;
  batch: string;
  pocs: string[];
}

/** Composite map key for a (cohort, batch) pair. */
const batchGroupKey = (cohort: string, batch: string): string =>
  `${cohort}\u0000${batch}`;

/**
 * Adds owning POCs to the (cohort, batch) bucket, creating it if needed.
 *
 * @param groups - Mutable (cohort, batch) → group map.
 * @param cohort - Target drug_cohort for the rule.
 * @param batch - The single batch/filename tag this group is keyed on.
 * @param pocs - POC names that own this batch (already availability-filtered).
 */
function addToBatchGroup(
  groups: Map<string, BatchGroup>,
  cohort: string,
  batch: string,
  pocs: string[],
): void {
  const key = batchGroupKey(cohort, batch);
  const group = groups.get(key) ?? { cohort, batch, pocs: [] };
  group.pocs.push(...pocs);
  groups.set(key, group);
}

/**
 * Groups rows into one bucket per (cohort, batch). Each bucket collects only the
 * available POCs that own that specific batch, so the generated rule can never
 * assign an order to a POC who does not own the matched batch.
 *
 * Rows with cohort "All" contribute to Green, Orange, and Unknown (no separate
 * "All" rule is emitted).
 *
 * @param rows - Current POC config table rows.
 * @returns Groups sorted by cohort then batch for deterministic output.
 */
function groupRowsByBatch(rows: PocConfigTableRowState[]): BatchGroup[] {
  const groups = new Map<string, BatchGroup>();

  rows.forEach((row) => {
    const cohort = row.cohort ?? "";
    const batches = (row.batch ?? []).filter(Boolean);
    const pocs =
      normalizeAvailability(row.availability) && row.poc ? [row.poc] : [];

    const cohorts = isAllCohort(cohort) ? ALL_COHORT_EXPAND_TARGETS : [cohort];
    cohorts.forEach((targetCohort) => {
      batches.forEach((batch) => {
        addToBatchGroup(groups, targetCohort, batch, pocs);
      });
    });
  });

  return Array.from(groups.values()).sort(
    (a, b) => a.cohort.localeCompare(b.cohort) || a.batch.localeCompare(b.batch),
  );
}

/**
 * Builds the poc_assignment rules-doc payload from the current table rows.
 *
 * Produces one rule per (cohort, batch). Each rule's condition is
 * `filename is any of [that single batch] AND cohort equals [cohort]`, and its
 * action sets a random POC from the available POCs that own that batch in that
 * cohort. Table rows with cohort "All" are expanded into Green, Orange, and
 * Unknown rules. Existing doc metadata (id, created_at, created_by) is preserved.
 *
 * @param rows - Current POC config table rows.
 * @param existingDoc - The existing poc_assignment doc, for metadata preservation.
 * @param updatedBy - Email to stamp as updated_by.
 * @param orgId - Selected health facility id, stamped on every rule as org_id.
 * @returns The full document payload to write to Firestore.
 */
export function buildPocAssignmentRunPayload(
  rows: PocConfigTableRowState[],
  existingDoc: PocAssignmentDoc | null,
  updatedBy?: string,
  orgId?: string,
): PocAssignmentDoc {
  const rules = groupRowsByBatch(rows)
    .filter((group) => group.pocs.length > 0)
    .map((group) => {
      const condition: QueryBuilderValue = {
        type: "group",
        id: uuidv4(),
        conjunction: "AND",
        children: [
          {
            type: "condition",
            id: uuidv4(),
            field: FILENAME_FIELD,
            operator: FILENAME_OPERATOR,
            value: [group.batch],
          },
          {
            type: "condition",
            id: uuidv4(),
            field: DRUG_COHORT_FIELD,
            operator: DRUG_COHORT_OPERATOR,
            value: group.cohort,
          },
          {
            type: "condition",
            id: uuidv4(),
            field: ORG_ID_FIELD,
            operator: ORG_ID_OPERATOR,
            value: orgId ?? "",
          },
        ],
      };

      return {
        label: `${group.cohort || "—"} · ${group.batch}`,
        subtext: `${RULE_SUBTEXT}: cohort ${group.cohort || "(none)"}, batch ${group.batch}`,
        active: true,
        triggers: [{ type: "order_start" }],
        actions: [
          {
            type: "set_random",
            field: POC_ACTION_FIELD,
            value: distinct(group.pocs),
          },
        ],
        rule: condition,
      };
    });

  return {
    id: existingDoc?.id ?? DEFAULT_POC_ASSIGNMENT_DOC_ID,
    rules,
    ...(existingDoc?.created_at != null && {
      created_at: existingDoc.created_at,
    }),
    ...(existingDoc?.created_by != null && {
      created_by: existingDoc.created_by,
    }),
    updated_at: new Date().toISOString(),
    ...(updatedBy != null && updatedBy !== "" && { updated_by: updatedBy }),
  };
}
